export interface PublicAuthUserPayload {
    userId: number;
    email: string;
    fullName?: string | null;
    tenantId: number;
    hasPassword: boolean;
    mustChangePassword: boolean;
}

export interface StoredPublicAuthSession {
    token: string;
    expiresAt: number;
    user: PublicAuthUserPayload;
}

const STORAGE_KEY = 'md-public-auth-sessions';

interface SessionDictionary {
    [tenantKey: string]: StoredPublicAuthSession;
}

const readAllSessions = (): SessionDictionary => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            return parsed as SessionDictionary;
        }
    } catch {
        /* ignore parse errors */
    }
    return {};
};

const writeSessions = (sessions: SessionDictionary) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch {
        /* ignore storage errors */
    }
};

export const resolveTenantKey = (tenantSlug?: string | null, tenantId?: string | null): string | null => {
    if (tenantSlug && tenantSlug.trim()) return tenantSlug.trim().toLowerCase();
    if (tenantId && tenantId.trim()) return tenantId.trim();
    return null;
};

export const loadSession = (tenantKey: string | null): StoredPublicAuthSession | null => {
    if (!tenantKey) return null;
    const sessions = readAllSessions();
    const session = sessions[tenantKey];
    if (!session) return null;
    if (session.expiresAt && session.expiresAt <= Date.now()) {
        delete sessions[tenantKey];
        writeSessions(sessions);
        return null;
    }
    return session;
};

export const saveSession = (tenantKey: string, value: StoredPublicAuthSession) => {
    const sessions = readAllSessions();
    sessions[tenantKey] = value;
    writeSessions(sessions);
};

export const clearSession = (tenantKey: string | null) => {
    if (!tenantKey) return;
    const sessions = readAllSessions();
    if (sessions[tenantKey]) {
        delete sessions[tenantKey];
        writeSessions(sessions);
    }
};

export const clearAllSessions = () => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
};
